import { useState } from 'react'
import { SkeletonList } from '../componentes/Skeleton'
import { EstadoError } from '../componentes/EstadoError'
import { EstadoVacio } from '../componentes/EstadoVacio'
import { TablaDatos, ayudanteColumnas } from '../componentes/TablaDatos'
import { IconoAmpliar } from '../componentes/iconos'
import { PerfilCliente } from '../componentes/PerfilCliente'
import { useClientes, type ClienteResumen } from '../hooks/useClientes'
import { useFiltrosCatalogo, type OpcionFiltro } from '../hooks/useFiltrosCatalogo'
import { useFiltrosUrl } from '../hooks/useFiltrosUrl'
import { BarraFiltros } from './logistica/BarraFiltros'
import { PanelMetricasLogistica } from './logistica/PanelMetricasLogistica'
import { GraficaProductos } from './logistica/GraficaProductos'
import { ModalTablaClientes } from './logistica/ModalTablaClientes'
import { TarjetaCliente } from './logistica/TarjetaCliente'

const ayudante = ayudanteColumnas<ClienteResumen>()

const COLUMNAS = ayudante.columns([
  ayudante.accessor('nombre', { header: 'Nombre' }),
  ayudante.accessor((fila) => fila.movil ?? '', { id: 'movil', header: 'Movil' }),
  ayudante.accessor((fila) => fila.direccion ?? '', { id: 'direccion', header: 'Direccion' }),
  ayudante.accessor((fila) => fila.ciudad ?? '', { id: 'ciudad', header: 'Poblacion' }),
  ayudante.accessor((fila) => fila.codigo_postal ?? '', { id: 'codigo_postal', header: 'C.P.' }),
])

function etiquetaDe(opciones: OpcionFiltro[], clave: string): string {
  return opciones.find((opcion) => opcion.clave === clave)?.etiqueta ?? clave
}

export function Logistica() {
  const { filtros, cambiarFiltro } = useFiltrosUrl()
  const clientes = useClientes(filtros)
  const catalogo = useFiltrosCatalogo()
  const [perfilId, setPerfilId] = useState<string | null>(null)
  const [modalAbierto, setModalAbierto] = useState(false)

  const semanas = catalogo.data?.semanas ?? []
  const rutas = catalogo.data?.rutas ?? []

  const filas = clientes.data?.items ?? []
  const total = clientes.data?.total ?? 0

  const activos: string[] = []
  if (filtros.q) activos.push(`busqueda "${filtros.q}"`)
  if (filtros.semana) activos.push(etiquetaDe(semanas, filtros.semana))
  if (filtros.ruta) activos.push(etiquetaDe(rutas, filtros.ruta))

  const estadoVacio = (
    <EstadoVacio
      titulo="No hay clientes con estos filtros"
      descripcion={
        activos.length > 0
          ? `Ningun cliente coincide con los filtros activos: ${activos.join(', ')}.`
          : 'El listado ha llegado vacio de la API.'
      }
      accion={
        filtros.q ? (
          <button
            type="button"
            onClick={() => cambiarFiltro('q', undefined)}
            className="rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado"
          >
            Limpiar la busqueda
          </button>
        ) : null
      }
    />
  )

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-xl font-semibold text-tinta">Logistica</h1>
        <p className="mt-1 text-sm text-tinta-apagada">
          Reparto por semana y ruta. Pulsa un cliente para ver su perfil; "Ampliar" abre la tabla completa.
        </p>
      </header>

      {/* Franja de métricas siempre visible (carga + calidad + seguimiento). */}
      <div data-tour="logistica-metricas" className="block">
        <PanelMetricasLogistica />
      </div>

      {/* Tabla a todo el ancho, DELIMITADA (altura fija, sin scroll infinito).
          "Ampliar" abre un modal con la tabla grande, todo el scroll y filtros.
          Un clic en un cliente abre su Perfil. */}
      <section data-tour="logistica-tabla" aria-label="Listado de clientes" className="flex min-w-0 flex-col gap-4">
        <BarraFiltros
          filtros={filtros}
          semanas={semanas}
          rutas={rutas}
          onCambiar={cambiarFiltro}
        />

        {clientes.isPending ? (
          <SkeletonList />
        ) : clientes.isError ? (
          <EstadoError error={clientes.error} onReintentar={() => void clientes.refetch()} />
        ) : filas.length === 0 ? (
          estadoVacio
        ) : (
          <>
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-tinta-apagada">
                {total} clientes · mostrando {filas.length}
              </p>
              <button
                type="button"
                onClick={() => setModalAbierto(true)}
                data-tour="logistica-ampliar"
                className="flex items-center gap-1.5 rounded-lg border border-borde-fuerte bg-superficie px-3 py-1.5 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado"
              >
                <IconoAmpliar className="size-4" />
                Ampliar
              </button>
            </div>

            {/* Tabla delimitada: altura acotada con scroll interno corto (no
                infinito). Para explorar todo, el usuario abre el modal. */}
            <div className="hidden max-h-[28rem] overflow-y-auto rounded-xl border border-borde lg:block">
              <TablaDatos
                datos={filas}
                columnas={COLUMNAS}
                onSeleccionar={(c) => setPerfilId(c.id)}
                seleccionadoId={null}
                estadoVacio={null}
                etiqueta="Clientes del reparto"
              />
            </div>

            {/* Tarjetas apiladas en movil, también delimitadas. */}
            <ul className="flex max-h-[28rem] flex-col gap-2 overflow-y-auto lg:hidden">
              {filas.map((cliente) => (
                <TarjetaCliente
                  key={cliente.id}
                  cliente={cliente}
                  seleccionado={false}
                  onSeleccionar={(c) => setPerfilId(c.id)}
                />
              ))}
            </ul>
          </>
        )}
      </section>

      {modalAbierto ? (
        <ModalTablaClientes
          onCerrar={() => setModalAbierto(false)}
          onElegir={(c) => {
            setModalAbierto(false)
            setPerfilId(c.id)
          }}
        />
      ) : null}

      <PerfilCliente contactId={perfilId} onCerrar={() => setPerfilId(null)} />

      {/* Fuera de la rejilla de dos columnas y de todo condicional: la grafica
          no depende de que haya un cliente seleccionado, ni de un filtro, ni
          del valor de retorno de un boton. Se monta una vez y se queda. */}
      <GraficaProductos />
    </div>
  )
}
